import { useRef, useState, useEffect, BaseSyntheticEvent } from 'react'
import styled from 'styled-components'
import { SubmitHandler, useForm, useFormState } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Link, Navigate, useParams, useSearchParams, useBlocker, useLocation } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { Tabs, Tab } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'

import SubmitButton from 'components/form/SubmitButton'
import SaveButton from 'components/form/SaveButton'
import { ArrowLeftIcon } from 'assets/icons/arrow-left'
import { ClientRoute } from 'routes/client'
import ChallengeFormPhase1 from 'components/form/phases/ChallengeFormPhase1'
import ChallengeFormPhase2 from 'components/form/phases/ChallengeFormPhase2'
import ChallengeFormPhase3 from 'components/form/phases/ChallengeFormPhase3'
import ChallengeFormPhase4 from 'components/form/phases/ChallengeFormPhase4'
import ChallengeFormPhase5 from 'components/form/phases/ChallengeFormPhase5'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import ErrorMessage from 'components/ui/ErrorMessage'
import { clearRedirect, clearChallenge } from 'store/challenge/slice'
import { getChallenge, createNewChallenge, updateChallenge, updateChallengeState } from 'store/challenge/thunks'
import { clearClubs, selectClubs } from 'store/clubs/slice'
import { challengeSchema } from 'validations/challengeSchema'
import { selectCountriesWithClubs } from 'store/countries/slice'
import { selectTimeZones } from 'store/timeZones/slice'
import { getTimeZones } from 'store/timeZones/thunks'
import {
  ChallengeComputedState,
  ChallengeControlledState,
  ChallengePayload,
  mapChallengeToFormData,
  mapFormDataToChallenge,
} from 'utils/challenges'
import { searchClubs } from 'store/clubs/thunks'
import { getCountriesWithClubs } from 'store/countries/thunks'
import { selectChallenge } from 'store/challenge/slice'
import { useAppDispatch } from 'store'
import { rem } from 'theme/materialUITheme'

interface ParamTypes extends ParamTypesBase {
  id: string
}

const DRAFT_BUTTON_ID = 'save-as-draft'
const PUBLISH_BUTTON_ID = 'publish'
const UNPUBLISH_BUTTON_ID = 'unpublish'

// Both buttons submit the same form, so the clicked one decides whether the challenge
// gets published or only stored as a draft. Anything but an explicit publish is a draft,
// which also covers submitting the form with the enter key.
const getSubmittedControlledState = (event?: BaseSyntheticEvent): ChallengeControlledState => {
  const submitter = (event?.nativeEvent as SubmitEvent | undefined)?.submitter ?? document.activeElement

  if (submitter?.id === PUBLISH_BUTTON_ID) {
    return ChallengeControlledState.PUBLISHED
  }

  if (submitter?.id === UNPUBLISH_BUTTON_ID) {
    return ChallengeControlledState.UNPUBLISHED
  }

  return ChallengeControlledState.DRAFT
}

const isClubsWithoutCourses = (clubs: Club[], data: any) => {
  const clubsWithoutCourses = clubs
    .filter((club: Club) => data.roundEligibility.clubIds.includes(club.id))
    .filter((club: Club) => {
      const clubCourses = club.courses.map((course: Course) => course.id).flat()
      return clubCourses.filter((courseId: number) => data.roundEligibility.courseIds.includes(courseId)).length === 0
    })

  return clubsWithoutCourses.length > 0
}

export default function ChallengeForm(): JSX.Element {
  const { id: paramId } = useParams<ParamTypes>()
  const id = paramId || ''
  const [searchParams, setSearchParams] = useSearchParams()
  const [phase, setPhase] = useState<string>(searchParams.get('phase') || '1')
  const [currentId, setCurrentId] = useState<string>('')
  const [validationError, setValidationError] = useState<string>('')
  const [initialFormSet, setInitialFormSet] = useState<boolean>(false)
  const initialSearch = useRef(false)
  // A club search replaces the club list in Redux and every phase remounts on a tab change,
  // so the club data (and with it the courses of each club) has to be accumulated here, above
  // the phases, to stay available for the whole editing session.
  const [clubsById, setClubsById] = useState<Record<number, Club>>({})
  const [loadingState, setLoadingState] = useState<string>('')
  const { countries, status: countriesStatus } = useSelector(selectCountriesWithClubs)
  const { clubs, status: selectClubsStatus } = useSelector(selectClubs)
  const { timeZones } = useSelector(selectTimeZones)

  const formElement = useRef<HTMLFormElement>(null)
  const { challenge, status, saveStatus, saveAsDraftStatus, redirectAfterCreate } = useSelector(selectChallenge)
  const form = useForm({
    shouldUnregister: false,
    resolver: yupResolver(challengeSchema),
    context: { computedState: challenge.computedState },
    disabled: challenge.computedState === ChallengeComputedState.COMPLETED,
  })

  const { handleSubmit, control, reset, watch } = form
  const formId = watch('id')
  const { errors, isDirty } = useFormState({ control })
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const location = useLocation()
  const isCreatingNewChallenge = location.pathname.endsWith('/new') || location.pathname.endsWith('new')
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return isDirty && currentLocation.pathname !== nextLocation.pathname
  })

  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (blocker.location.pathname.endsWith('/edit')) {
        blocker.proceed()
        return
      }
      const confirmed = window.confirm(intl.formatMessage({ id: 'strings.leavePageConfirmation' }))
      if (confirmed) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker, intl])

  useEffect(() => {
    if (formId === '-1' || id) {
      return
    }
    if (!id && formId !== '-1') {
      dispatch(clearChallenge())
      reset({
        ...challengeSchema.getDefault(),
      })
    }
    dispatch(clearClubs())
  }, [id, reset, dispatch, clubs, countries, formId])

  useEffect(() => {
    if (id && (status === 'idle' || currentId !== id)) {
      setCurrentId(id)
      dispatch(getChallenge(id))
    } else if ((id && saveStatus === 'succeeded') || saveAsDraftStatus === 'succeeded') {
      dispatch(clearRedirect())
    }
  }, [id, currentId, status, reset, dispatch, challenge, saveStatus, saveAsDraftStatus])

  useEffect(() => {
    if (countriesStatus === 'idle') {
      dispatch(getCountriesWithClubs())
    }
  }, [countriesStatus, dispatch])

  useEffect(() => {
    if (timeZones.length === 0) {
      dispatch(getTimeZones())
    }
  }, [timeZones, dispatch])

  // Every phase resolves its club and course labels from the same club list, so the initial
  // fetch has to cover the clubs of all of them at once. Fetching only the clubs of the phase
  // that happened to be open made the mapping below drop the other phases' selections.
  useEffect(() => {
    if (selectClubsStatus !== 'idle' || status !== 'succeeded' || initialSearch.current) {
      return
    }

    const clubIds = Array.from(
      new Set(
        [
          ...(challenge.roundEligibility?.clubIds ?? []),
          ...(challenge.audienceControl?.joinAudience?.clubIds ?? []),
          ...(challenge.audienceControl?.extraViewAudience?.clubIds ?? []),
        ].map((clubId: string) => Number(clubId)),
      ),
    )

    if (clubIds.length === 0) {
      return
    }

    initialSearch.current = true
    dispatch(searchClubs({ data: { clubIds, activeCourses: true } }))
  }, [selectClubsStatus, status, challenge, dispatch])

  useEffect(() => {
    if (clubs.length === 0) {
      return
    }

    setClubsById((previous) => {
      const next = { ...previous }
      clubs.forEach((club: Club) => {
        next[club.id] = club
      })
      return next
    })
  }, [clubs])

  // The club list is shared with the other scenes that search clubs, so it is cleared when the
  // form goes away. Clearing it per phase would drop the clubs of the phase that is not open.
  useEffect(() => {
    return () => {
      dispatch(clearClubs())
      dispatch(clearChallenge())
    }
  }, [dispatch])

  // Set form data once challenge, countries, and clubs are ready
  useEffect(() => {
    if (isCreatingNewChallenge && !initialFormSet) {
      dispatch(clearChallenge())
      reset(challengeSchema.getDefault())
      setInitialFormSet(true)
      return
    }
    if (initialFormSet || status !== 'succeeded' || countriesStatus !== 'succeeded') {
      return
    }
    if (challenge.challengeId === '-1' || (id && id !== challenge.challengeId)) {
      return
    }

    const hasClubIds =
      challenge.roundEligibility?.clubIds?.length || challenge.audienceControl?.joinAudience?.clubIds?.length

    // If challenge references clubs, wait for them to be fetched
    if (hasClubIds && selectClubsStatus !== 'succeeded') {
      return
    }

    // Don't overwrite form during a save
    if (saveStatus !== 'idle' || saveAsDraftStatus !== 'idle') {
      return
    }

    reset(mapChallengeToFormData(challenge, clubs, countries, intl, timeZones) as any)
    setInitialFormSet(true)
  }, [
    id,
    challenge,
    status,
    countriesStatus,
    selectClubsStatus,
    initialFormSet,
    saveStatus,
    saveAsDraftStatus,
    reset,
    intl,
    clubs,
    countries,
  ])

  const handleChange = (_event, tabValue) => {
    setPhase(tabValue)
    setSearchParams((prev) => {
      prev.set('phase', tabValue)
      return prev
    })
    initialSearch.current = false
  }

  const submitForm: SubmitHandler<Yup.InferType<typeof challengeSchema>> = (formData, event) => {
    setValidationError('')
    setLoadingState(getSubmittedControlledState())

    const payloadData: ChallengePayload = {
      ...mapFormDataToChallenge(formData, countries, timeZones),
    }

    if (isClubsWithoutCourses(clubs, payloadData)) {
      const error = intl.formatMessage({ id: 'challenges.clubWithoutCoursesValidationError' })
      setValidationError(error)
      return
    }

    const submittedControlledState = getSubmittedControlledState(event)

    const submittedValues = form.getValues()
    const markAsSaved = () => reset(submittedValues, { keepValues: true })

    if (id) {
      const payload: UpdateChallengePayload = {
        challengeId: id,
        successMessage: intl.formatMessage({ id: 'challenges.saveSuccess' }),
        data: {
          ...payloadData,
        },
      }
      dispatch(updateChallenge(payload))
        .unwrap()
        .then((response) => {
          markAsSaved()
          maybeTransitionChallengeState(
            id,
            submittedControlledState,
            response.data?.controlledState ?? ChallengeControlledState.UNSPECIFIED,
          )
        })
        .catch(() => undefined)
    } else {
      const payload: CreateChallengePayload = {
        successMessage: intl.formatMessage({ id: 'challenges.saveSuccess' }),
        data: {
          ...{
            ...payloadData,
            controlledState: submittedControlledState,
            category: challenge?.category ?? 'CHALLENGE_CATEGORY_SPONSORED',
          },
        },
      }
      dispatch(createNewChallenge(payload))
        .unwrap()
        .then((response) => {
          markAsSaved()
          if (response.data?.challengeId) {
            setCurrentId(response.data.challengeId)
            maybeTransitionChallengeState(
              response.data.challengeId,
              submittedControlledState,
              response.data.controlledState ?? ChallengeControlledState.UNSPECIFIED,
            )
          }
        })
        .catch(() => undefined)
    }
  }

  // Saving the challenge does not change its controlled state, so publishing/unpublishing is a
  // separate request. The backend only allows draft -> published, published -> unpublished,
  // and unpublished -> published, so we must only send a transition when it is valid.
  const maybeTransitionChallengeState = (
    challengeId: string,
    submittedControlledState: ChallengeControlledState,
    currentControlledState: string,
  ) => {
    if (
      submittedControlledState === ChallengeControlledState.PUBLISHED &&
      currentControlledState !== ChallengeControlledState.PUBLISHED
    ) {
      dispatch(
        updateChallengeState({
          challengeId,
          controlledState: ChallengeControlledState.PUBLISHED,
          successMessage: intl.formatMessage({ id: 'challenges.publishSuccess' }),
        }),
      )
      return
    }

    if (
      submittedControlledState === ChallengeControlledState.UNPUBLISHED &&
      currentControlledState === ChallengeControlledState.PUBLISHED
    ) {
      dispatch(
        updateChallengeState({
          challengeId,
          controlledState: ChallengeControlledState.UNPUBLISHED,
          successMessage: intl.formatMessage({ id: 'challenges.unpublishSuccess' }),
        }),
      )
    }
  }

  const getSubmitButtonText = () => {
    if (
      challenge &&
      (challenge.computedState === ChallengeComputedState.LIVE ||
        challenge.computedState === ChallengeComputedState.VISIBLE ||
        challenge.computedState === ChallengeComputedState.UPCOMING)
    ) {
      return intl.formatMessage({ id: 'strings.save' })
    }
    return intl.formatMessage({ id: 'strings.publish' })
  }

  const extractNestedErrors = (errors: any, parentKey: string = ''): Array<{ field: string; message: string }> => {
    const result: Array<{ field: string; message: string }> = []

    Object.entries(errors).forEach(([key, value]: [string, any]) => {
      const fullPath = parentKey ? `${parentKey}.${key}` : key

      if (value?.message) {
        result.push({ field: fullPath, message: value.message })
      } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        result.push(...extractNestedErrors(value, fullPath))
      }
    })

    return result
  }

  const maybeRenderErrorMessages = (): JSX.Element | null => {
    if (Object.keys(errors).length === 0) {
      return null
    }

    const nestedErrors = extractNestedErrors(errors)

    if (nestedErrors.length === 0) {
      const fieldNames = Object.keys(errors).join(', ')
      return fieldNames ? (
        <CustomErrorMessage>
          {intl.formatMessage({ id: 'strings.validationErrorsInFields' }, { fields: fieldNames })}
        </CustomErrorMessage>
      ) : null
    }

    return (
      <CustomErrorMessage>
        <>
          {nestedErrors.map((error, index) => (
            <div key={index}>
              <strong>{error.field}:</strong> {error.message}
            </div>
          ))}
        </>
      </CustomErrorMessage>
    )
  }

  if (redirectAfterCreate) {
    return <Navigate to={`/admin/challenges/${challenge.challengeId}/edit`} />
  }

  return (
    <PageContainer>
      <BackBar>
        <BackLink to={ClientRoute.ADMIN_CHALLENGES}>
          <ArrowLeftIcon />
          <FormattedMessage id="strings.backToChallenges" />
        </BackLink>
      </BackBar>
      <TabBar>
        <TabContainer value={phase} onChange={handleChange}>
          <Tab value="1" label={intl.formatMessage({ id: 'challenges.typeAndScheduling' })} />
          <Tab value="2" label={intl.formatMessage({ id: 'challenges.targeting' })} />
          <Tab value="3" label={intl.formatMessage({ id: 'challenges.descriptions' })} />
          <Tab value="4" label={intl.formatMessage({ id: 'challenges.branding' })} />
          <Tab value="5" label={intl.formatMessage({ id: 'challenges.optIn' })} />
        </TabContainer>
      </TabBar>

      {status === 'loading' || (challenge.challengeId !== '-1' && !id) ? (
        <SpinnerContainer>
          <LoadingSpinnerGreen size={72} />
        </SpinnerContainer>
      ) : (
        <form onSubmit={handleSubmit(submitForm)} ref={formElement}>
          {phase === '1' && <ChallengeFormPhase1 challenge={challenge} form={form} clubsById={clubsById} />}
          {phase === '2' && <ChallengeFormPhase2 challenge={challenge} form={form} />}
          {phase === '3' && <ChallengeFormPhase3 form={form} />}
          {phase === '4' && <ChallengeFormPhase4 challenge={challenge} disabled={form.formState.disabled} />}
          {phase === '5' && <ChallengeFormPhase5 challenge={challenge} form={form} />}

          {!form.formState.disabled && (
            <FooterContainer>
              {maybeRenderErrorMessages()}

              {validationError && <CustomErrorMessage>{validationError}</CustomErrorMessage>}

              <FooterButtonRow>
                {(challenge?.controlledState === ChallengeControlledState.DRAFT ||
                  challenge?.controlledState === ChallengeControlledState.UNSPECIFIED ||
                  challenge?.controlledState === undefined) && (
                  <SaveButton
                    status={loadingState === ChallengeControlledState.DRAFT ? saveStatus : 'idle'}
                    text={intl.formatMessage({ id: 'strings.saveAsDraft' })}
                    id={DRAFT_BUTTON_ID}
                  />
                )}
                {challenge?.controlledState === ChallengeControlledState.PUBLISHED && (
                  <SaveButton
                    status={loadingState === ChallengeControlledState.UNPUBLISHED ? saveStatus : 'idle'}
                    text={intl.formatMessage({ id: 'strings.unpublish' })}
                    id={UNPUBLISH_BUTTON_ID}
                  />
                )}
                <StyledSubmitButton
                  // Publishing saves first and then changes the state, so the button has to stay
                  // in its loading state for both requests
                  status={loadingState === ChallengeControlledState.PUBLISHED ? saveStatus : 'idle'}
                  text={getSubmitButtonText()}
                  id={PUBLISH_BUTTON_ID}
                />
              </FooterButtonRow>
            </FooterContainer>
          )}
        </form>
      )}
    </PageContainer>
  )
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${rem(48)};
`

const BackBar = styled.div`
  align-self: stretch;
  background: white;
  border-bottom: 1px solid var(--border, #d8d8d8);
  padding: 16px;
  display: flex;
  align-items: center;
`

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 12px;
  color: var(--neutral-600, #525252);
  font-family: var(--family-sans, Söhne);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-decoration: none;

  &:hover {
    background: var(--neutral-100, #f5f5f5);
  }
`

const TabBar = styled.div`
  align-self: stretch;
  position: relative;
  background: white;
  border-bottom: 1px solid var(--border, #e4e4e7);
  padding: 0 24px;
`

const TabContainer = styled(Tabs)`
  width: 100%;

  & .MuiTabs-indicator {
    height: 0;
  }

  & .MuiTabs-flexContainer {
    gap: 16px;
  }

  & button {
    margin: 0;
    padding: 0 4px 16px;
    border-bottom: 2px solid transparent;
    text-transform: none;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: var(--muted-foreground, #8a8a8e);

    &.Mui-selected {
      color: var(--primary, #0bb163);
      border-color: var(--primary, #0bb163);
    }
  }
`

const SpinnerContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 50px auto;
  justify-content: center;
`

const CustomErrorMessage = styled(ErrorMessage)`
  margin-bottom: 20px;
`

const FooterContainer = styled.div`
  align-self: stretch;
  background: white;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const FooterButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const StyledSubmitButton = styled(SubmitButton)`
  padding: var(--p-25, 8px) var(--p-4, 12px);
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: var(--rounded-xl, 12px);
  border: 1px solid var(--Green-default, #0bb163);
  background: var(--Green-default, #0bb163);

  /* Shadow/xs */
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
`
